import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Pagination,
    Card,
    CardContent,
    Grid
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from "sweetalert2";

const PAGE_SIZE = 10;

function Dealer() {
    const [dealers, setDealers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        dealerName: "",
        mobile: "",
        email: "",
        address: "",
        city: "",
        state: "",
        gstName: "",
        gstNo: "",
    });

    // Fetch dealers
    useEffect(() => {
        loadDealers();
    }, []);

    const loadDealers = async () => {
        const snap = await getDocs(collection(db, "dealers"));
        setDealers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!form.dealerName || !form.mobile) {
            Swal.fire("Error", "Please fill required fields", "error");
            return;
        }

        try {
            if (editingId) {
                await updateDoc(doc(db, "dealers", editingId), form);
                Swal.fire("Success", "Dealer updated", "success");
            } else {
                await addDoc(collection(db, "dealers"), form);
                Swal.fire("Success", "Dealer added", "success");
            }

            setOpenDialog(false);
            setEditingId(null);
            setForm({
                dealerName: "",
                mobile: "",
                email: "",
                address: "",
                city: "",
                state: "",
                gstName: "",
                gstNo: "",
            });

            loadDealers();
        } catch (err) {
            Swal.fire("Error", err.message, "error");
        }
    };

    const handleEdit = (d) => {
        setEditingId(d.id);
        setForm(d);
        setOpenDialog(true);
    };

    const handleDelete = async (id) => {
        const res = await Swal.fire({
            title: "Delete dealer?",
            icon: "warning",
            showCancelButton: true,
        });
        if (!res.isConfirmed) return;
        await deleteDoc(doc(db, "dealers", id));
        Swal.fire("Deleted", "Dealer removed", "success");
        loadDealers();
    };

    // Filter & paginate
    const filtered = dealers.filter(
        d =>
            d.dealerName?.toLowerCase().includes(search.toLowerCase()) ||
            d.mobile?.includes(search) ||
            d.email?.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box p={2} bgcolor="#f4f6f8" minHeight="100vh">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={800}>
                    Dealer Management
                </Typography>
                <Button variant="contained" onClick={() => setOpenDialog(true)}>
                    Add New Dealer
                </Button>
            </Box>

            <TextField
                label="Search by name, mobile, email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Mobile</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Address</TableCell>
                                <TableCell>City</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>GST Name</TableCell>
                                <TableCell>GST No</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((d, index) => (
                                <TableRow key={d.id}>
                                    <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                    <TableCell>{d.dealerName}</TableCell>
                                    <TableCell>{d.mobile}</TableCell>
                                    <TableCell>{d.email || "-"}</TableCell>
                                    <TableCell>{d.address || "-"}</TableCell>
                                    <TableCell>{d.city || "-"}</TableCell>
                                    <TableCell>{d.state || "-"}</TableCell>
                                    <TableCell>{d.gstName || "-"}</TableCell>
                                    <TableCell>{d.gstNo || "-"}</TableCell>
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => handleEdit(d)}>
                                            <EditOutlined />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(d.id)}>
                                            <DeleteOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={Math.ceil(filtered.length / PAGE_SIZE)}
                            page={page}
                            onChange={(e, v) => setPage(v)}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingId ? "Edit Dealer" : "Add New Dealer"}</DialogTitle>
                <DialogContent>
                    <Grid className="row" spacing={2} sx={{ mt: 1 }}>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="Dealer Name"
                                name="dealerName"
                                value={form.dealerName}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="Mobile"
                                name="mobile"
                                value={form.mobile}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                required
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="Address"
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="City"
                                name="city"
                                value={form.city}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="State"
                                name="state"
                                value={form.state}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="GST Name"
                                name="gstName"
                                value={form.gstName}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid className="col-sm-3 mb-3">
                            <TextField
                                label="GST No"
                                name="gstNo"
                                value={form.gstNo}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>
                        {editingId ? "Update" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Dealer;